import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Cpu, Truck, Factory, Shield, Building, Briefcase,
  Smartphone, Laptop, Headphones, Music,
  Car, Wrench, Settings, Zap, Droplet,
  Camera, Bell, HardHat, Layers, Hammer,
  ShoppingBag, Package, FileText, ChevronRight
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Icon mapping
const iconMap = {
  cpu: Cpu,
  truck: Truck,
  factory: Factory,
  shield: Shield,
  building: Building,
  briefcase: Briefcase,
  smartphone: Smartphone,
  laptop: Laptop,
  headphones: Headphones,
  music: Music,
  car: Car,
  wrench: Wrench,
  settings: Settings,
  zap: Zap,
  droplet: Droplet,
  camera: Camera,
  bell: Bell,
  'hard-hat': HardHat,
  layers: Layers,
  hammer: Hammer,
  'shopping-bag': ShoppingBag,
  package: Package,
  'file-text': FileText
};

/**
 * Category Card Component
 * Displays a single category with image, icon, and name
 */
export const CategoryCard = ({ category, size = 'medium' }) => {
  const { language } = useLanguage();
  const isEn = language === 'en';
  
  const name = isEn ? category.name_en : (category.name_pt || category.name_en);
  const Icon = iconMap[category.icon] || Cpu;
  
  const sizeClasses = {
    small: 'h-32',
    medium: 'h-40',
    large: 'h-48'
  };
  
  return (
    <Link
      to={`/products?category=${category.slug}`}
      className={`
        group relative overflow-hidden rounded-xl bg-white border border-slate-200
        hover:border-[#F59E0B] hover:shadow-lg transition-all duration-300
        ${sizeClasses[size]}
      `}
      data-testid={`category-card-${category.slug}`}
    >
      {/* Background Image */}
      {category.image_url ? (
        <div className="absolute inset-0">
          <img 
            src={category.image_url} 
            alt={name}
            className="w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/80 to-transparent" />
        </div>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-slate-100" />
      )}
      
      {/* Content */}
      <div className="relative h-full flex flex-col items-center justify-center p-4 text-center">
        <div className="w-14 h-14 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center mb-3 group-hover:bg-[#F59E0B]/20 transition-colors">
          <Icon className="w-7 h-7 text-[#1E3A8A] group-hover:text-[#F59E0B] transition-colors" />
        </div>
        <h3 className="font-semibold text-slate-800 group-hover:text-[#1E3A8A] transition-colors">
          {name}
        </h3>
        {category.product_count > 0 && (
          <p className="text-xs text-slate-500 mt-1">
            {category.product_count} {isEn ? 'products' : 'produtos'}
          </p>
        )}
      </div>
      
      {/* Hover Arrow */}
      <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
        <ChevronRight className="w-5 h-5 text-[#F59E0B]" />
      </div>
    </Link>
  );
};

/**
 * Category Grid Component
 * Displays categories in a responsive grid layout
 */
export const CategoryGrid = ({ categories, columns = 6, showTitle = true }) => {
  const { language } = useLanguage();
  const isEn = language === 'en';
  
  // Filter only main categories (level 1)
  const mainCategories = categories.filter(cat => !cat.parent_id && cat.level === 1);
  
  const gridCols = {
    3: 'grid-cols-2 sm:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-4',
    6: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6'
  };
  
  return (
    <section className="py-8">
      {showTitle && (
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-heading font-bold text-slate-800">
            {isEn ? 'Shop by Category' : 'Comprar por Categoria'}
          </h2>
          <Link 
            to="/products" 
            className="text-[#1E3A8A] hover:text-[#F59E0B] font-medium flex items-center gap-1 transition-colors"
          >
            {isEn ? 'View All' : 'Ver Todos'}
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      )}
      
      <div className={`grid ${gridCols[columns] || gridCols[6]} gap-4`}>
        {mainCategories.map(category => (
          <CategoryCard key={category.category_id} category={category} />
        ))}
      </div>
    </section>
  );
};

/**
 * Category Sidebar Component
 * Shows category tree for filtering in product pages
 */
export const CategorySidebar = ({ categories, activeSlug, onCategorySelect }) => {
  const { language } = useLanguage();
  const isEn = language === 'en';
  
  // Build tree
  const mainCategories = categories.filter(cat => !cat.parent_id);
  const getChildren = (parentId) => categories.filter(cat => cat.parent_id === parentId);
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="category-sidebar">
      <h3 className="font-semibold text-slate-800 mb-4">
        {isEn ? 'Categories' : 'Categorias'}
      </h3>
      
      <nav className="space-y-1">
        {/* All Products */}
        <button
          onClick={() => onCategorySelect?.(null)}
          className={`
            w-full text-left px-3 py-2 rounded-lg transition-colors
            ${!activeSlug ? 'bg-[#1E3A8A]/10 text-[#1E3A8A] font-medium' : 'hover:bg-slate-50 text-slate-700'}
          `}
        >
          {isEn ? 'All Products' : 'Todos os Produtos'}
        </button>
        
        {mainCategories.map(category => {
          const children = getChildren(category.category_id);
          const isActive = activeSlug === category.slug;
          const name = isEn ? category.name_en : (category.name_pt || category.name_en);
          
          return (
            <div key={category.category_id}>
              <button
                onClick={() => onCategorySelect?.(category.slug)}
                className={`
                  w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center justify-between
                  ${isActive ? 'bg-[#1E3A8A]/10 text-[#1E3A8A] font-medium' : 'hover:bg-slate-50 text-slate-700'}
                `}
              >
                <span>{name}</span>
                {children.length > 0 && (
                  <ChevronRight className={`w-4 h-4 transition-transform ${isActive ? 'rotate-90' : ''}`} />
                )}
              </button>
              
              {/* Subcategories */}
              {children.length > 0 && (isActive || children.some(c => c.slug === activeSlug)) && (
                <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-100 pl-3">
                  {children.map(child => {
                    const childName = isEn ? child.name_en : (child.name_pt || child.name_en);
                    const isChildActive = activeSlug === child.slug;
                    
                    return (
                      <button
                        key={child.category_id}
                        onClick={() => onCategorySelect?.(child.slug)}
                        className={`
                          w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors
                          ${isChildActive ? 'bg-[#F59E0B]/10 text-[#F59E0B] font-medium' : 'hover:bg-slate-50 text-slate-600'}
                        `}
                      >
                        {childName}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
};

/**
 * Subcategory Pills Component
 * Horizontal scrollable subcategory pills for mobile
 */
export const SubcategoryPills = ({ subcategories, activeSlug, onSelect }) => {
  const { language } = useLanguage();
  const isEn = language === 'en';
  
  if (!subcategories || subcategories.length === 0) return null;
  
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
      <button
        onClick={() => onSelect?.(null)}
        className={`
          flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors
          ${!activeSlug 
            ? 'bg-[#1E3A8A] text-white' 
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }
        `}
      >
        {isEn ? 'All' : 'Todos'}
      </button>
      
      {subcategories.map(sub => {
        const name = isEn ? sub.name_en : (sub.name_pt || sub.name_en);
        const isActive = activeSlug === sub.slug;
        
        return (
          <button
            key={sub.category_id}
            onClick={() => onSelect?.(sub.slug)}
            className={`
              flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors
              ${isActive 
                ? 'bg-[#F59E0B] text-white' 
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }
            `}
          >
            {name}
          </button>
        );
      })}
    </div>
  );
};

export default CategoryGrid;
